import { computed, ref, watch, type Ref } from 'vue';
import useVuelidate, {
  type ValidationRuleWithoutParams,
} from '@vuelidate/core';
import { createI18nMessage, required, url } from '@vuelidate/validators';
import { isValidPhoneNumber } from 'libphonenumber-js';

import type { SharingFormData } from './useSharingForm';
import { zodEmailValidator } from '@/utils/emailValidation';

export type SharingTouchpoint = {
  label: string;
  type: string;
};

export const useSharingValidation = (params: {
  formData: SharingFormData;
  t: (key: string) => string;
  needsCampaignSelection: Ref<boolean>;
  availableTouchpoints: Ref<SharingTouchpoint[]>;
}) => {
  const { formData, t, needsCampaignSelection, availableTouchpoints } = params;
  const withI18nMessage = createI18nMessage({ t });

  const optionalValue = (
    validator:
      | ((value: string) => boolean)
      | ValidationRuleWithoutParams<string>
  ) => {
    return (value: string, siblingState: unknown, vm: unknown) => {
      if (!value) {
        return true;
      }

      // Handle vuelidate validators that have $validator property
      if (typeof validator === 'object' && '$validator' in validator) {
        return validator.$validator(value, siblingState, vm);
      }

      // Handle regular functions
      return validator(value);
    };
  };

  const touchpointRules = computed(() => {
    const rules: Record<string, object> = {};

    for (const touchpoint of availableTouchpoints.value) {
      if (touchpoint.type === 'phoneNumber') {
        rules[touchpoint.label] = {
          validPhoneNumber: withI18nMessage(
            (value: string) => {
              if (!value) {
                return true;
              }

              return isValidPhoneNumber(value);
            },
            {
              messagePath: () =>
                t('generic.phoneNumber') + t('validation.valid'),
            }
          ),
        };
      } else if (touchpoint.type === 'email') {
        rules[touchpoint.label] = {
          email: withI18nMessage(zodEmailValidator, {
            messagePath: () => t('validation.invalidEmail'),
          }),
        };
      } else {
        rules[touchpoint.label] = {
          url: withI18nMessage(optionalValue(url), {
            messagePath: () => t('validation.urlValidation'),
          }),
        };
      }
    }

    return rules;
  });

  const rules = computed(() => ({
    selectedCampaign: needsCampaignSelection.value
      ? {
          required: withI18nMessage(required, {
            messagePath: () => t('validation.value'),
          }),
        }
      : {},
    signUp: {
      firstname: {
        required: withI18nMessage(required, {
          messagePath: () => t('validation.value'),
        }),
      },
      lastname: {
        required: withI18nMessage(required, {
          messagePath: () => t('validation.value'),
        }),
      },
      email: {
        email: withI18nMessage(zodEmailValidator, {
          messagePath: () => t('validation.invalidEmail'),
        }),
        required: withI18nMessage(required, {
          messagePath: () => t('validation.value'),
        }),
      },
      phoneNumber: {
        required: withI18nMessage(required, {
          messagePath: () => t('validation.value'),
        }),
        validPhoneNumber: withI18nMessage(
          (value: string) => isValidPhoneNumber(value),
          {
            messagePath: () => t('generic.phoneNumber') + t('validation.valid'),
          }
        ),
      },
      criteria: {
        required: withI18nMessage(required, {
          messagePath: () => t('validation.value'),
        }),
      },
      touchpoints: {
        ...touchpointRules.value,
      },
      consent: {
        required: withI18nMessage((value: boolean) => value === true, {
          messagePath: () => t('validation.value'),
        }),
      },
    },
  }));

  const v$ = useVuelidate(rules, formData);
  const touchpointWatchStops = ref<Array<() => void>>([]);

  watch(
    () => formData.signUp.firstname,
    async () => await v$.value.signUp.firstname.$validate()
  );

  watch(
    () => formData.signUp.lastname,
    async () => await v$.value.signUp.lastname.$validate()
  );

  watch(
    () => formData.signUp.email,
    async () => await v$.value.signUp.email.$validate()
  );

  watch(
    () => formData.signUp.phoneNumber,
    async (value) => {
      const validator = v$.value.signUp.phoneNumber;
      if (!validator) return;

      if (!validator.$dirty && !value) {
        return;
      }

      await validator.$validate();
    }
  );

  const setupTouchpointWatchers = (touchpoints: SharingTouchpoint[]) => {
    touchpointWatchStops.value.forEach((stop) => stop());
    touchpointWatchStops.value = [];

    for (const touchpoint of touchpoints) {
      const stop = watch(
        () => formData.signUp.touchpoints[touchpoint.label],
        async () => {
          const validator = v$.value.signUp.touchpoints?.[touchpoint.label];
          if (validator) {
            await validator.$validate();
          }
        }
      );

      touchpointWatchStops.value.push(stop);
    }
  };

  watch(
    availableTouchpoints,
    (touchpoints) => {
      setupTouchpointWatchers(touchpoints || []);
    },
    { immediate: true }
  );

  return {
    v$,
  };
};
